import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { PermissionsService, PermissionType } from '@qtek/libs/permissions';
import { POPOUT_WINDOW } from '@qtek/libs/popout';
import {
  AiChatActions,
  AppState,
  selectChatsWithPreview,
  selectCreatedChatIdByMysid,
} from '@qtek/libs/store';
import { TranslationCoreModule } from '@qtek/libs/translation-core';
import { AiChat, POPOUT_MODAL_DATA } from '@qtek/shared/models';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AlloMemoryNoticeService } from '../../services/allo-memory-notice.service';
import { AiChatComponent } from '../ai-chat/ai-chat.component';
import { AiChatHistoryComponent } from '../ai-chat-history/ai-chat-history.component';
import { AiChatSettingsComponent } from '../ai-chat-settings/ai-chat-settings.component';

const AI_CHAT_POPOUT_STORAGE_KEY = 'aiChatPopoutState';

interface AiChatPopoutState {
  chatId: string | null;
  windowId: string;
  startNew: boolean;
  lazySubscribe: boolean;
}

export interface AiChatPopoutData {
  chatId?: string | null;
  windowId?: string | null;
  startNew?: boolean;
  lazySubscribe?: boolean;
}

@Component({
  selector: 'qt-ai-chat-popout',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    TranslationCoreModule,
    AiChatComponent,
    AiChatHistoryComponent,
    AiChatSettingsComponent,
  ],
  templateUrl: './ai-chat-popout.component.html',
  styleUrls: ['./ai-chat-popout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatPopoutComponent implements OnInit, OnDestroy {
  readonly showChat = signal(false);
  readonly showHistory = signal(false);
  /** SD-2096: the Allo settings drop-down (cross-session memory controls). */
  readonly showSettings = signal(false);
  readonly selectedChatId = signal<string | null>(null);
  readonly chatSessionKey = signal(this.createWindowId());
  readonly lazyChatSubscribe = signal(false);

  private readonly destroy$ = new Subject<void>();
  private readonly chatFeature = 'ai-agent';
  private readonly permissions = inject(PermissionsService);
  private readonly memoryNotices = inject(AlloMemoryNoticeService);
  /** SD-2096: dot on the settings button while Allo's memory changed unseen. */
  readonly unseenMemoryChange = this.memoryNotices.unseenChange;
  private readonly store = inject(Store<AppState>);
  private readonly data = inject(POPOUT_MODAL_DATA, { optional: true }) as
    | AiChatPopoutData
    | undefined;
  private readonly popoutWindow = inject(POPOUT_WINDOW, { optional: true });
  private lastChatHistoryMysid: string | null = null;
  private initialApplied = false;
  /** Chat ID passed when opening popout; never clear this from the list subscription */
  private initialChatId: string | null = null;

  constructor() {
    if (this.data?.windowId) {
      this.chatSessionKey.set(this.data.windowId);
    }

    this.store
      .select(selectChatsWithPreview)
      .pipe(takeUntil(this.destroy$))
      .subscribe(chats => {
        const normalized = (chats ?? []).filter(
          (chat): chat is AiChat => !!chat?.id
        );

        const current = this.selectedChatId();
        if (
          current &&
          current !== this.initialChatId &&
          !normalized.some(chat => chat.id === current)
        ) {
          this.selectedChatId.set(null);
        }
      });

    this.permissions
      .isAllowed$(this.chatFeature, PermissionType.View)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isAllowed => {
        this.showChat.set(isAllowed);
        if (isAllowed) {
          this.requestChatHistoryIfNeeded();
          this.applyInitialSelection();
        }
      });

    if (this.data?.lazySubscribe) {
      this.lazyChatSubscribe.set(true);
    }
  }

  ngOnInit(): void {
    this.applyInitialSelection();

    // SD-2096: while the chat lives out here, the memory-changed notice is this window's badge
    // alone — a toast would be raised into the main window the user has just left.
    if (this.popoutWindow) {
      this.memoryNotices.registerPopoutWindow(this.popoutWindow);
    }

    // SD-2096: "Review" on the memory-changed toast — open the panel it refers to.
    this.memoryNotices.reviewRequested$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.openSettings());
  }

  startNewChat(): void {
    if (!this.showChat()) return;
    this.showHistory.set(false);
    this.showSettings.set(false);
    this.lazyChatSubscribe.set(true);
    this.chatSessionKey.set(this.createWindowId());
    this.selectedChatId.set(null);
    this.requestChatHistoryIfNeeded();
    this.persistPopoutState({
      chatId: null,
      windowId: this.chatSessionKey(),
      startNew: true,
      lazySubscribe: this.lazyChatSubscribe(),
    });
  }

  toggleHistory(): void {
    if (!this.showChat()) return;
    this.showHistory.set(!this.showHistory());
    // History and settings share the drop-down slot under the header — showing both at once
    // would push the transcript off-screen.
    if (this.showHistory()) this.showSettings.set(false);
  }

  /** SD-2096: open/close the Allo settings drop-down. */
  toggleSettings(): void {
    if (!this.showChat()) return;
    this.showSettings.set(!this.showSettings());
    if (this.showSettings()) this.showHistory.set(false);
  }

  /** SD-2096: open the settings drop-down (no-op when it already is), for the toast's action. */
  openSettings(): void {
    if (!this.showChat() || this.showSettings()) return;
    this.toggleSettings();
  }

  /**
   * SD-2661: the history list deleted a chat. Deleting the thread you are reading leaves the
   * panel pointing at nothing, so drop into a fresh chat rather than an empty transcript.
   *
   * `selectedChatId` only covers a thread picked from history. A chat started here is created
   * inside `qt-ai-chat` on the first prompt and lands solely in the store under this session's
   * mysid, so deleting one of those has to be recognised through that map — otherwise the window
   * keeps the deleted chat open and the next prompt fails with "Chat not found".
   */
  onChatDeleted(chatId: string): void {
    if (this.selectedChatId() === chatId) {
      if (this.initialChatId === chatId) {
        this.initialChatId = null;
      }
      this.startNewChat();
      return;
    }

    this.store
      .select(selectCreatedChatIdByMysid(`chats_${this.chatSessionKey()}`))
      .pipe(take(1))
      .subscribe(createdId => {
        if (createdId === chatId) {
          this.startNewChat();
        }
      });
  }

  selectChat(chatId: string): void {
    if (!this.showChat()) return;
    this.showHistory.set(false);
    this.showSettings.set(false);
    this.lazyChatSubscribe.set(false);
    this.selectedChatId.set(chatId);
    this.chatSessionKey.set(this.createWindowId());

    const payload: Parameters<
      typeof AiChatActions.getChatEvents
    >[0]['payload'] = {
      mysid: `chatEvent_${chatId}`,
      prms: { chatId },
    };

    this.store.dispatch(AiChatActions.getChatEvents({ payload }));
    this.persistPopoutState({
      chatId,
      windowId: this.chatSessionKey(),
      startNew: false,
      lazySubscribe: this.lazyChatSubscribe(),
    });
  }

  closeWindow(): void {
    if (typeof window !== 'undefined') {
      window.close();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getChatIdFromUrl(): string | null {
    try {
      const w = this.popoutWindow;
      if (!w?.location?.search) return null;
      const params = new URLSearchParams(w.location.search);
      return params.get('chatId');
    } catch {
      return null;
    }
  }

  private applyInitialSelection(): void {
    if (this.initialApplied) return;
    this.initialApplied = true;

    const chatId = this.data?.chatId ?? this.getChatIdFromUrl() ?? null;

    if (chatId) {
      this.initialChatId = chatId;
      this.lazyChatSubscribe.set(false);
      this.selectedChatId.set(chatId);
      this.chatSessionKey.set(this.createWindowId());
      this.store.dispatch(
        AiChatActions.getChatEvents({
          payload: {
            mysid: `chatEvent_${chatId}`,
            prms: { chatId },
          },
        })
      );
      this.requestChatHistoryIfNeeded();
      return;
    }

    if (this.data?.startNew || this.data?.lazySubscribe) {
      this.lazyChatSubscribe.set(true);
      this.chatSessionKey.set(this.createWindowId());
      this.selectedChatId.set(null);
      this.requestChatHistoryIfNeeded();
    }
  }

  private requestChatHistoryIfNeeded(): void {
    const mysid = `chats_${this.chatSessionKey()}`;
    if (this.lastChatHistoryMysid === mysid) {
      return;
    }

    const payload: Parameters<typeof AiChatActions.getChats>[0]['payload'] = {
      mysid,
    };

    this.store.dispatch(AiChatActions.getChats({ payload }));
    this.lastChatHistoryMysid = mysid;
  }

  private createWindowId(): string {
    return `popout-ai-${Date.now().toString(16)}-${Math.random()
      .toString(16)
      .slice(2, 10)}`;
  }

  private persistPopoutState(state: AiChatPopoutState): void {
    if (typeof window === 'undefined') return;
    try {
      const opener = window.opener;
      if (!opener || opener.closed || !opener.sessionStorage) {
        return;
      }

      opener.sessionStorage.setItem(
        AI_CHAT_POPOUT_STORAGE_KEY,
        JSON.stringify(state)
      );

      opener.postMessage(
        {
          type: 'ai-chat-popout-state',
          state,
        },
        window.location.origin
      );
    } catch {
      // Ignore cross-window or storage errors.
    }
  }
}
